import { createFileRoute } from '@tanstack/react-router';
import * as z from 'zod';
import { userQueries, userTabSchema } from '@/entities/user';
import { UsersPage } from '@/pages/users';
import { requireAuth } from '../require-auth';

/**
 * Сотрудники — the merchant's staff roster.
 *
 * Lives under `/profile`, not under a sub-app: the roster is merchant-wide
 * (one identity works across Eats and Доставка alike), so scoping it to either
 * namespace would imply a per-sub-app staff list that does not exist. It is
 * reached from the profile screen's «Сотрудники» card.
 *
 * The URL is the single source of truth (CLAUDE.md state-boundary table): the
 * active role `tab` (defaults to «Все») and the free-text `search` both live
 * in the URL, so both survive reload and deep-link.
 */
const usersSearchSchema = z.object({
  tab: userTabSchema.default('all'),
  search: z.string().optional(),
});

export const Route = createFileRoute('/profile_/users')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts,
  // so no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: usersSearchSchema,
  // Fire-and-forget prefetch — see `routes/index.tsx`.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(userQueries.list());
  },
  component: UsersRoute,
});

function UsersRoute() {
  const { tab, search } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <UsersPage
      activeTab={tab}
      search={search}
      // Preserve the current search when switching tabs.
      onTabChange={(next) =>
        void navigate({ search: (prev) => ({ ...prev, tab: next }) })
      }
    />
  );
}
